// Admin CMS — /api/admin/cms + /api/admin/tags (ff-api admin blueprint)

export type CMSFieldType =
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'url'
  | 'select'
  | 'multiselect'
  | 'json';

export type CMSDisplayStatus = 'draft' | 'published' | 'expired' | 'deleted' | 'inactive';

export interface CMSAsset {
  asid: number | string;
  public_url?: string;
  mime_type?: string;
  filename?: string;
  width?: number | null;
  height?: number | null;
  size_bytes?: number | null;
  alt_text?: string | null;
  date_added?: string;
  featured?: boolean;
  order_by?: number;
  [key: string]: unknown;
}

export interface CMSTag {
  tagid: number;
  name: string;
  synonyms?: string[];
  description?: string | null;
  tagid_parent?: number | null;
  etid_json?: number[] | null;
  attributes?: Record<string, unknown> | unknown[] | null;
  name_other?: Record<string, string> | null;
  in_use?: boolean | null;
  active?: boolean | null;
  protected?: boolean | null;
  autogen?: boolean | null;
  locale?: string | null;
  uuid?: string | null;
  date_added?: string | null;
  date_modified?: string | null;
  entity_count?: number;
  cms_count?: number;
  total_count?: number;
  source?: string;
  [key: string]: unknown;
}

export interface TagUsageSummary {
  tagid: number;
  entity_count: number;
  cms_count: number;
  total_count: number;
  [key: string]: unknown;
}

export interface TagListFilters {
  q?: string;
  limit?: number;
  offset?: number;
  include_inactive?: boolean | string;
  active?: boolean | string;
  protected?: boolean | string;
  in_use?: boolean | string;
  autogen?: boolean | string;
  locale?: string;
  tagid_parent?: number | string;
  etid?: number | string;
  sort_by?: 'name' | 'date_added' | 'date_modified' | 'tagid' | 'usage' | string;
  sort_order?: 'asc' | 'desc';
  include_usage?: boolean | string;
}

export interface TagListResponse {
  tags: CMSTag[];
  pagination?: {
    limit?: number;
    offset?: number;
    count?: number;
    total?: number;
  };
}

export interface CMSContentType {
  cctid?: number;
  key: string;
  label: string;
  description?: string | null;
  sort_order?: number;
  active?: boolean;
  protected?: boolean;
  [key: string]: unknown;
}

export interface CMSTemplate {
  ctplid: number;
  name: string;
  name_system?: string | null;
  ctype?: string | null;
  title_tpl?: string | null;
  summary?: string | null;
  content?: string | null;
  tagid?: number[] | null;
  fields_json?: Record<string, unknown> | null;
  active?: boolean;
  [key: string]: unknown;
}

export interface CMSFieldDefinition {
  cfdid: number;
  key: string;
  label: string;
  field_type: CMSFieldType | string;
  ctype?: string | null;
  required?: boolean;
  sort_order?: number;
  options_json?: unknown;
  active?: boolean;
  [key: string]: unknown;
}

export interface CMSPageField extends CMSFieldDefinition {
  value: unknown;
}

export interface CMSMenuLink {
  menid: number;
  menid_root?: number | null;
  menid_parent?: number | null;
  name?: string;
  name_system?: string | null;
  cmsoid?: number | null;
  locale?: string | null;
  active?: boolean;
  order_by?: number | null;
  date_modified?: string | null;
  [key: string]: unknown;
}

export interface CMSRevision {
  cmshid: number;
  cmsoid?: number;
  title?: string;
  summary?: string | null;
  content?: string | null;
  slug?: string;
  ctype?: string;
  locale?: string | null;
  country_iso2?: string | null;
  author?: string | null;
  date_added?: string;
  [key: string]: unknown;
}

export interface CMSSlugHistory {
  cshid: number;
  cmsoid: number;
  oid?: number | null;
  slug: string;
  country_iso2?: string | null;
  date_added?: string | null;
}

export interface CMSObject {
  cmsoid: number;
  title: string;
  summary?: string | null;
  content: string;
  slug: string;
  ctype: string;
  uid?: number | null;
  oid?: number | null;
  eid?: number | null;
  asid?: number | null;
  parent_cmsoid?: number | null;
  sort_order?: number | null;
  tagid?: number[] | null;
  author?: string | null;
  authors?: unknown;
  locale?: string | null;
  country_iso2?: string | null;
  active: boolean;
  published: boolean;
  deleted: boolean;
  date_added?: string;
  date_modified?: string;
  date_published?: string | null;
  date_expired?: string | null;
  Assets?: CMSAsset[];
  Menus?: CMSMenuLink[];
  Fields?: CMSPageField[];
  Tags?: CMSTag[];
  /** Client-derived */
  status?: CMSDisplayStatus;
  hero_url?: string | null;
}

export interface CMSListFilters {
  status?: CMSDisplayStatus | '';
  ctype?: string;
  locale?: string;
  country_iso2?: string;
  q?: string;
  only_published?: boolean;
  include_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateCMSPayload {
  title: string;
  content?: string;
  summary?: string;
  slug?: string;
  ctype?: string;
  oid?: number;
  eid?: number;
  asid?: number;
  parent_cmsoid?: number;
  locale?: string | null;
  country_iso2?: string | null;
  tagid?: number[];
  date_published?: string | null;
  date_expired?: string | null;
}

export interface UpdateCMSPayload extends Partial<CreateCMSPayload> {
  cmsoid: number;
  active?: boolean | number;
  published?: boolean | number;
}

export interface MediaListFilters {
  q?: string;
  mime?: 'image' | 'video' | 'all';
}

export interface MediaListResponse {
  assets: CMSAsset[];
  total_assets: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface UploadMediaResponse {
  status?: number;
  data?: CMSAsset;
  assets?: CMSAsset[];
  message?: string;
  [key: string]: unknown;
}

export const CMS_LOCALES = ['', 'en_GB', 'en_US', 'fr_FR', 'es_ES', 'pt_BR', 'de_DE'] as const;
export const CMS_COUNTRIES = ['', 'US', 'GB', 'NG', 'KE', 'ZA', 'FR', 'DE'] as const;

export const CMS_FIELD_TYPES: CMSFieldType[] = [
  'text',
  'textarea',
  'richtext',
  'number',
  'boolean',
  'date',
  'datetime',
  'url',
  'select',
  'multiselect',
  'json',
];

export function publicContentUrl(slug: string): string {
  const origin = (import.meta.env.VITE_PUBLIC_SITE_ORIGIN as string | undefined)?.replace(/\/$/, '');
  const path = slug.startsWith('/') ? slug : `/content/${slug}`;
  return origin ? `${origin}${path}` : path;
}

export function deriveCMSStatus(page: Pick<CMSObject, 'deleted' | 'active' | 'published' | 'date_expired'>): CMSDisplayStatus {
  if (page.deleted) return 'deleted';
  if (page.active === false) return 'inactive';
  if (page.date_expired) {
    const t = new Date(page.date_expired).getTime();
    if (!Number.isNaN(t) && t < Date.now()) return 'expired';
  }
  if (!page.published) return 'draft';
  return 'published';
}

export function normalizeCMSObject(raw: any): CMSObject {
  if (!raw || typeof raw !== 'object') return raw as CMSObject;
  const published = !!raw.published;
  const active = raw.active !== false && raw.active !== 0;
  const deleted = !!raw.deleted;
  const Assets = Array.isArray(raw.Assets)
    ? raw.Assets
    : Array.isArray(raw.asset)
      ? raw.asset
      : [];
  const featured = raw.asset_ ?? Assets.find((a: CMSAsset) => a.featured) ?? null;
  const asid = raw.asid ?? featured?.asid ?? null;
  const page: CMSObject = {
    ...raw,
    published,
    active,
    deleted,
    asid: asid != null ? Number(asid) : null,
    Assets,
    hero_url: featured?.public_url ?? raw.hero_url ?? null,
    content: raw.content ?? '',
    title: raw.title ?? '',
    slug: raw.slug ?? '',
    ctype: raw.ctype ?? 'page',
  };
  page.status = deriveCMSStatus(page);
  return page;
}
